import React, { useState, useRef, useEffect } from 'react';
import "mapbox-gl/dist/mapbox-gl.css";
import '../../services/isMobile.js';
import { convertDateToString, sign, convertDurationToString, convertTripDistanceToString, convertDurationMillisToString } from '../../common/ConvertTools.js';
import { getBestTimeStr } from '../../common/BestAccel.js';
import EventBus from "../../common/EventBus";
import DatabaseService from '../../services/database.service.js';

const PerfsCard = React.memo(({ contentRunSelected, contentData, batVariationSag2s, batVariationSag10s, selectedCircuitId, refreshLapsAndCircuitData, liteMode, isDesktop }) => {
  const [markers, setMarkers] = useState([]);
  const markerPositionsRef = useRef(markers);

  useEffect(() => {
    const handleMarkersData = (newMarkers) => {
      console.log("PerfsCard / handleMarkersData", newMarkers);
      setMarkers(newMarkers);
      markerPositionsRef.current = newMarkers;
    };

    EventBus.on("handleMarkersData", handleMarkersData);

    return () => {
      EventBus.remove("handleMarkersData", handleMarkersData);
    };
  }, []);

  if (contentRunSelected === undefined) {
    return null;
  }

  const lapTimes = markers.slice(1).map((lap, index) => {
    if (contentData.length < markers[index + 1])
      return null;
    const start = contentData[markers[index]];
    const end = contentData[markers[index + 1]];
    return end.date - start.date;
  }).filter(Boolean);

  const saveLaps = () => {
    console.log("saveLaps / contentRunSelectedTimeStamp", contentRunSelected.timestamp, "selectedCircuit", selectedCircuitId, "markers", markers);
    DatabaseService.editPerfsRunLaps(contentRunSelected.id, selectedCircuitId, markers);

    refreshLapsAndCircuitData(selectedCircuitId, markers);
  }


  const minLapTime = Math.min(...lapTimes);
  const maxLapTime = Math.max(...lapTimes);

  return (
    <>
      <div className="col-md-2">
        <div className="card mt-0">
          <div className="card-body flex-wrap">
            <h5 className="card-title">Session details</h5>
            <p className="card-text">Date: {convertDateToString(contentRunSelected.timestamp / 1000)}</p>
            <p className="card-text">Duration: {convertDurationToString(contentRunSelected.duration / 1000)}</p>
            <p className="card-text">Device name: {contentRunSelected.ble_name}</p>
            <p className="card-text">Distance: {convertTripDistanceToString(contentRunSelected.distance)}</p>
            {liteMode && <p className="card-text">Registered to: {contentRunSelected.owner_email ? contentRunSelected.owner_email : "unknown"}</p>}
            {liteMode && <p className="card-text">Errors: {contentRunSelected.hadErrors > 0 ? "yes" : "no"}</p>}
          </div>
        </div>
      </div>
      <div className="col-md-2">
        <div className="card mt-0">
          <div className="card-body">
            <h5 className="card-title">Max speed & power</h5>
            <p className="card-text">Speed: {contentRunSelected.maxSpeed} km/h</p>
            {liteMode && <p className="card-text">Speed GPS: {contentRunSelected.maxSpeedGps} km/h</p>}
            <p className="card-text">Power: {contentRunSelected.maxPower} W</p>
            <p className="card-text">Current: {contentRunSelected.maxCurrent} A</p>
            <p className="card-text">Phase current master: {contentRunSelected.maxCurrentMot1} A</p>
            <p className="card-text">Phase current slave: {contentRunSelected.maxCurrentMot2} A</p>
          </div>
        </div>
      </div>
      <div className="col-md-2">
        <div className="card mt-0">
          <div className="card-body">
            <h5 className="card-title">Battery</h5>
            <p className="card-text">Sag 2s: {batVariationSag2s}</p>
            <p className="card-text">Sag 10s: {batVariationSag10s}</p>
            <p className="card-text">Session variation: {sign(contentRunSelected?.batVariationStartEnd?.toFixed(2))} V</p>
          </div>
        </div>
      </div>
      <div className="col-md-2">
        <div className="card mt-0">
          <div className="card-body">
            <h5 className="card-title">Acceleration</h5>
            <p className="card-text">Max: {contentRunSelected.maxAcceleration} m/s² / {(contentRunSelected?.maxAcceleration / 9.81)?.toFixed(2)} g</p>
            <p className="card-text">0-50 Km/h: {getBestTimeStr(contentData, 0, 50)}</p>
            <p className="card-text">0-70 Km/h: {getBestTimeStr(contentData, 0, 70)}</p>
            <p className="card-text">0-100 Km/h: {getBestTimeStr(contentData, 0, 100)}</p>
            <p className="card-text">0-130 Km/h: {getBestTimeStr(contentData, 0, 130)}</p>
          </div>
        </div>
      </div>
      <div className="col-md-2">
        <div className="card mt-0">
          <div className="card-body">
            <h5 className="card-title">Temperatures</h5>
            <p className="card-text">Controller max: {contentRunSelected?.maxTempContrl} °C / var: {sign(contentRunSelected?.tempContrlStartEnd?.toFixed(0))} °C</p>
            <p className="card-text">Motor(s) max: {contentRunSelected?.maxTempMotor} °C / var: {sign(contentRunSelected?.tempMotorStartEnd?.toFixed(0))} °C</p>
            <p className="card-text">BMS int 1 max: {contentRunSelected?.maxBmsIntTemp1} °C / var: {sign((contentRunSelected?.maxBmsIntTemp1 - contentRunSelected?.minBmsIntTemp1).toFixed(0))} °C</p>
            <p className="card-text">BMS int 2 max: {contentRunSelected?.maxBmsIntTemp2} °C / var: {sign((contentRunSelected?.maxBmsIntTemp2 - contentRunSelected?.minBmsIntTemp2).toFixed(0))} °C</p>
            <p className="card-text">BMS bat 1 max: {contentRunSelected?.maxBmsBatTemp1} °C / var: {sign((contentRunSelected?.maxBmsBatTemp1 - contentRunSelected?.minBmsBatTemp1).toFixed(0))} °C</p>
            <p className="card-text">BMS bat 2 max: {contentRunSelected?.maxBmsBatTemp2} °C / var: {sign((contentRunSelected?.maxBmsBatTemp2 - contentRunSelected?.minBmsBatTemp2).toFixed(0))} °C</p>
          </div>
        </div>
      </div>
      <div className="col-md-2">
        <div className="card mt-0">
          <div className="card-body card-body-scrollable">
            <div className="d-flex justify-content-between">
              <h5 className="card-title">Laps</h5>
              {isDesktop && (
                <button
                  className="btn btn-outline-primary m-1"
                  onClick={() => saveLaps()}
                >
                  Save
                </button>
              )}
              </div>
              {markers.length > 1 ? (
                markers.map((lap, index) => {
                  if (index === 0) {
                    return null;
                  } else {
                    if (contentData.length < markers[index]) return null;
                    const start = contentData[markers[index - 1]];
                    const end = contentData[markers[index]];
                    const diff = end?.date - start?.date;
                    const lapTime = convertDurationMillisToString(diff);
                    const lapStyle = {
                      color: diff === minLapTime ? 'green' : diff === maxLapTime ? 'red' : '',
                      fontWeight: diff === minLapTime ? 'bold' : diff === maxLapTime ? 'bold' : '',
                    };
                    return <p className="card-text" key={index} style={lapStyle}>Lap {index}: {lapTime}</p>;
                  }
                })
              ) : (
                isDesktop ? (
                  <p className="card-text">At least 2 markers are needed to display laps</p>
                ) : (
                  <p className="card-text">No recorded laps</p>
                )
              )}
            </div>
          </div>
        </div>

    </>
  )
})

export default PerfsCard;
